import React, { useEffect, useState } from 'react';
import {
  Sparkles, Flame, Clock, CheckCircle2, ArrowUpRight, ArrowDownRight, Loader2,
} from 'lucide-react';
import Sparkline from './Sparkline';
import { cn } from '../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const KPI_CONFIG = [
  {
    key: 'new_leads',
    label: 'New Leads',
    icon: Sparkles,
    accent: 'text-crm-blue',
    suffix: '',
    testId: 'lead-nurture-kpi-new',
  },
  {
    key: 'hot_leads',
    label: 'Hot Leads',
    icon: Flame,
    accent: 'text-rose-500',
    suffix: '',
    testId: 'lead-nurture-kpi-hot',
  },
  {
    key: 'avg_response_hours',
    label: 'Avg Response',
    icon: Clock,
    accent: 'text-amber-500',
    suffix: 'h',
    testId: 'lead-nurture-kpi-response',
    invertDelta: true, // lower hours = better, so positive delta is bad
  },
  {
    key: 'ready_to_apply',
    label: 'Ready To Apply',
    icon: CheckCircle2,
    accent: 'text-emerald-500',
    suffix: '',
    testId: 'lead-nurture-kpi-ready',
  },
];

const formatValue = (v, suffix) => {
  if (v === null || v === undefined) return '—';
  if (typeof v !== 'number') return v;
  if (suffix === 'h') {
    if (v === 0) return '—';
    if (v >= 100) return `${Math.round(v)}h`;
    return `${v.toFixed(1)}h`;
  }
  return v.toLocaleString();
};

const KPICard = ({ config, data, loading }) => {
  const { label, icon: Icon, accent, suffix, testId, invertDelta } = config;
  const metric = data || {};
  const value = metric.value ?? 0;
  const deltaPct = Number(metric.delta_pct ?? 0);
  const sparkline = Array.isArray(metric.sparkline) ? metric.sparkline : [];

  // For Avg Response: a positive delta is bad (slower response), so flip the colours
  const isPositive = invertDelta ? deltaPct < 0 : deltaPct > 0;
  const isNeutral = deltaPct === 0;
  const DeltaIcon = isPositive ? ArrowUpRight : ArrowDownRight;
  const deltaClasses = isNeutral
    ? 'text-slate-400 dark:text-slate-500'
    : isPositive
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-rose-600 dark:text-rose-400';

  return (
    <div
      className="relative flex flex-col justify-between p-5 rounded-xl border border-surface-border dark:border-slate-800 bg-white dark:bg-slate-900/60 shadow-sm overflow-hidden min-h-[140px]"
      data-testid={testId}
    >
      <div className="relative z-10 flex items-start justify-between">
        <div>
          <div className={cn('flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold', accent)}>
            <Icon className="w-3.5 h-3.5" />
            <span>{label}</span>
          </div>
          <div className="mt-2 text-3xl font-display font-extrabold tracking-tight text-slate-900 dark:text-white">
            {loading ? <Loader2 className="w-6 h-6 animate-spin text-slate-300" /> : formatValue(value, suffix)}
          </div>
        </div>
        <span className={cn('inline-flex items-center gap-0.5 text-xs font-semibold whitespace-nowrap', deltaClasses)}>
          <DeltaIcon className="w-3 h-3" />
          {Math.abs(deltaPct).toFixed(1)}%
        </span>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mt-3">
        vs prior 14 days
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[60px] opacity-50 pointer-events-none">
        <Sparkline data={sparkline} colour={accent} height={60} />
      </div>
    </div>
  );
};

const NurtureKPIStrip = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/nurture/kpi-strip?days=14`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await r.json();
        if (!cancelled) setData(d || {});
      } catch (e) {
        if (!cancelled) setData({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="lead-nurture-kpi-strip">
      {KPI_CONFIG.map((cfg) => (
        <KPICard key={cfg.key} config={cfg} data={data?.[cfg.key]} loading={loading} />
      ))}
    </div>
  );
};

export default NurtureKPIStrip;
